import { MUIDataTableBody, TableBody as DefaultTableBody } from "mui-datatables";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { computeWindow, WindowRange } from "./windowedRows";

/**
 * A drop-in `components.TableBody` for mui-datatables that bounds the mounted
 * `<tr>` count by spacer-row virtualization — leg B of
 * operator-task-fe-logs-tab-bounded-dom-virtualization.
 *
 * ## Why this shape
 * The Logs tab renders ALL accumulated gradual-log rows as one un-paginated list
 * (`pagination: false`), so a high-volume record could grow an unbounded number
 * of `<tr>`s in the DOM. mui-datatables 4.3.0 has no built-in virtualization.
 * Rather than hand-rebuild rows/cells (which would have to reproduce the exact
 * MUI/withStyles cell classes the sticky header, column-width alignment, resize
 * handles, horizontal-scroll sync and log-level row coloring all depend on), this
 * component REUSES the stock {@link DefaultTableBody} for the visible slice and
 * only brackets it with two empty full-width spacer rows that reserve the scroll
 * extent of the unmounted rows. Every mounted row/cell is therefore byte-identical
 * to today's rendering; only the row COUNT is bounded.
 *
 * The spacer cells use `colSpan` over all columns, so they never participate in
 * the `table-layout: auto` per-column width computation — no column jitter as
 * rows window in and out, and the single shared `<table>` (sticky `<thead>` +
 * body) is left structurally intact.
 *
 * ## Self-calibrating
 * The uniform row height (leg A made every log row one fixed height) and the
 * list's content-space top offset are measured live from the first mounted real
 * row (never hardcoded), and the scroll position is read from the nearest
 * scrollable ancestor resolved at runtime (handles both the inner
 * `responsiveBase` scroll container and full-page scroll), mirroring the
 * {@link StickyHorizontalScrollbar} resolution pattern.
 *
 * Installed ONLY by the logs listing (via `options` → RecordTable →
 * `components.TableBody`); every other list in the app keeps the stock body.
 */

// Rows to keep mounted above/below the viewport so a fast scroll doesn't flash
// un-mounted gaps before the next measure pass catches up.
const OVERSCAN = 8;
// Row-height fallback (px) used only for the very first paint, before a real row
// has been measured. Corrected on the next layout pass.
const ROW_HEIGHT_FALLBACK = 48;
// mui-datatables stamps every body row with this test id prefix; used to locate
// the first mounted real row to measure from.
const BODY_ROW_SELECTOR = '[data-testid^="MUIDataTableBodyRow-"]';

const SCROLLABLE_OVERFLOW = /(auto|scroll|overlay)/;

function getScrollParent(node: HTMLElement | null): HTMLElement | null {
    let el = node?.parentElement ?? null;
    while (el) {
        const styleDecl = window.getComputedStyle(el);
        if (
            SCROLLABLE_OVERFLOW.test(styleDecl.overflowY) &&
            el.scrollHeight > el.clientHeight
        ) {
            return el;
        }
        el = el.parentElement;
    }
    return null;
}

type DisplayRow = { data: unknown[]; dataIndex: number };

export default function WindowedTableBody(props: MUIDataTableBody) {
    const { data, columns, options } = props;
    const rows = (data as unknown as DisplayRow[]) ?? [];
    const rowCount = rows.length;

    const [range, setRange] = useState<WindowRange>({
        start: 0,
        end: rowCount,
        topHeight: 0,
        bottomHeight: 0,
    });
    // Mirror of the latest range so measure() can read it without being
    // recreated on every range change (it must stay referentially stable).
    const rangeRef = useRef(range);
    rangeRef.current = range;

    const topSpacerRef = useRef<HTMLTableSectionElement>(null);
    const rowHeightRef = useRef<number>(ROW_HEIGHT_FALLBACK);
    const listTopRef = useRef<number>(0);
    const scrollParentRef = useRef<HTMLElement | null>(null);
    const scrollParentResolvedRef = useRef<boolean>(false);
    const rafRef = useRef<number | null>(null);

    // Recompute the visible window from the current scroll position, measuring
    // the row height + list offset from the first mounted real row. Only commits
    // a state change when the window actually moves, so it converges (a changed
    // window re-mounts rows, which this re-measures once more to refine the
    // offset, then stabilises).
    const measure = useCallback(() => {
        const anchor = topSpacerRef.current;
        const table = anchor?.closest("table") ?? null;
        if (!table) return;

        if (!scrollParentResolvedRef.current) {
            scrollParentRef.current = getScrollParent(table);
            scrollParentResolvedRef.current = true;
        }
        const scrollEl = scrollParentRef.current;
        const useWindow = !scrollEl;
        const scrollTop = useWindow
            ? document.scrollingElement?.scrollTop ?? window.scrollY
            : scrollEl!.scrollTop;
        const viewportHeight = useWindow
            ? window.innerHeight
            : scrollEl!.clientHeight;
        const scrollRectTop = useWindow ? 0 : scrollEl!.getBoundingClientRect().top;

        const firstRow = table.querySelector<HTMLElement>(BODY_ROW_SELECTOR);
        if (firstRow) {
            const h = firstRow.offsetHeight;
            if (h > 0) rowHeightRef.current = h;
            const rowTopContent =
                firstRow.getBoundingClientRect().top - scrollRectTop + scrollTop;
            // The first mounted real row sits at index range.start, so its
            // content-top === listTop + start*rowHeight -> solve for listTop.
            listTopRef.current = rowTopContent - rangeRef.current.start * rowHeightRef.current;
        }

        const next = computeWindow({
            scrollTop,
            viewportHeight,
            listTop: listTopRef.current,
            rowHeight: rowHeightRef.current,
            rowCount,
            overscan: OVERSCAN,
        });
        const cur = rangeRef.current;
        if (
            next.start !== cur.start ||
            next.end !== cur.end ||
            next.topHeight !== cur.topHeight ||
            next.bottomHeight !== cur.bottomHeight
        ) {
            setRange(next);
            // Re-measure after the new rows mount to refine listTop/rowHeight.
            if (rafRef.current == null) {
                rafRef.current = window.requestAnimationFrame(() => {
                    rafRef.current = null;
                    measure();
                });
            }
        }
    }, [rowCount]);

    // Re-measure after each data change (rows loaded/cleared) and on mount.
    useLayoutEffect(() => {
        measure();
    }, [measure, rowCount]);

    // Scroll/resize listeners. Capture-phase document scroll catches ANY ancestor
    // scroll container (scroll events do not bubble), matching StickyHorizontalScrollbar.
    useEffect(() => {
        const onScrollOrResize = () => {
            if (rafRef.current != null) return;
            rafRef.current = window.requestAnimationFrame(() => {
                rafRef.current = null;
                measure();
            });
        };
        document.addEventListener("scroll", onScrollOrResize, {
            capture: true,
            passive: true,
        });
        window.addEventListener("resize", onScrollOrResize, { passive: true });

        const table = topSpacerRef.current?.closest("table") ?? null;
        const ro = new ResizeObserver(onScrollOrResize);
        if (table) ro.observe(table);
        if (scrollParentRef.current) ro.observe(scrollParentRef.current);

        return () => {
            document.removeEventListener("scroll", onScrollOrResize, {
                capture: true,
            } as EventListenerOptions);
            window.removeEventListener("resize", onScrollOrResize);
            ro.disconnect();
            if (rafRef.current != null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [measure]);

    // Columns currently displayed, plus the select/expand lead column if present,
    // give the spacer cell's colSpan so it stretches the full table width.
    const shownColumns = (columns as Array<{ display?: string | boolean }>).filter(
        (col) => col.display === "true" || col.display === true
    ).length;
    const hasLeadColumn =
        (options.selectableRows && options.selectableRows !== "none") ||
        Boolean(options.expandableRows);
    const colSpan = Math.max(1, shownColumns + (hasLeadColumn ? 1 : 0));

    // Empty list: defer entirely to the stock body so its "no matching data"
    // empty state renders unchanged.
    if (rowCount === 0) {
        return <DefaultTableBody {...props} />;
    }

    const windowSlice = rows.slice(range.start, range.end) as unknown as MUIDataTableBody["data"];

    const spacer = (height: number, key: string, ref?: typeof topSpacerRef) => (
        <tbody ref={ref} aria-hidden="true" className="recordTableWindowSpacer" key={key}>
            <tr style={{ height }}>
                <td colSpan={colSpan} style={{ height, padding: 0, border: 0 }} />
            </tr>
        </tbody>
    );

    return (
        <>
            {spacer(range.topHeight, "window-spacer-top", topSpacerRef)}
            <DefaultTableBody
                {...props}
                data={windowSlice}
                count={windowSlice.length}
                page={0}
                rowsPerPage={windowSlice.length}
            />
            {spacer(range.bottomHeight, "window-spacer-bottom")}
        </>
    );
}
